import { useCallback, useMemo, useState, type FC } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRight, History, Pencil, Plus } from 'lucide-react';
import type { ShipmentDto } from '@/api';
import {
  Alert,
  Button,
  DebouncedSearchInput,
  Table,
  TablePagination,
  cn,
  useTableSort,
  type TableColumn,
} from '@/ui';

import { ShipmentOriginBadge } from '../../components/ShipmentBadges';
import { ShipmentLinesCell } from '../../components/ShipmentLinesCell';
import { TopbarActions } from '../../components/Topbar';
import { resolveApiErrorMessage } from '../../lib/apiError';
import { formatDate, formatMoney, toNumber } from '../../lib/format';
import { formatSort } from '../../lib/tableSort';
import {
  EditShipmentDialog,
  ShipmentEditorDialog,
  useTakeShipmentIntoWork,
} from '../shipment-actions';
import { usePlannedShipments } from './api/queries';
import { HistoryPlanDialog } from './HistoryPlanDialog';
import {
  PAGE_SIZE,
  resolvePlanSearch,
  type PeriodFilter,
  type PlanSearch,
} from './lib/searchParams';

const ROUTE_ID = '/_app/plan' as const;
const ROUTE_PATH = '/plan' as const;

const PERIODS: ReadonlyArray<{ value: PeriodFilter; label: string }> = [
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'all', label: 'Всё' },
];

/*
 * План отгрузок - PlanPage макета: прогноз-заявки в «Запланировано». Отсюда
 * отгрузка создаётся, правится и берётся в работу («Согласование»).
 * Автоматического перехода за месяц до даты пока нет - только кнопка.
 *
 * «Сформировать из истории» (на месте кнопки «Загрузить из 1С» макета) - мастер
 * плана из истории: отгрузки по ритму прошлых поставок каждого товара
 * (plan/shipment-plan-from-history).
 */
export const PlanPage: FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: ROUTE_ID });
  const resolved = resolvePlanSearch(search);

  const [isCreating, setIsCreating] = useState(false);
  const [isPlanningFromHistory, setIsPlanningFromHistory] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const takeIntoWork = useTakeShipmentIntoWork();

  const setSearch = useCallback(
    (patch: Partial<PlanSearch>, resetPage = true) => {
      void navigate({
        from: ROUTE_PATH,
        to: ROUTE_PATH,
        search: (previous: PlanSearch) => ({
          ...previous,
          ...patch,
          ...(resetPage ? { page: undefined } : null),
        }),
      });
    },
    [navigate],
  );

  const { toggleSort } = useTableSort(resolved.sort, (next) =>
    setSearch({ sort: formatSort(next) }),
  );

  const { data, isPending, isError, error } = usePlannedShipments(resolved);

  const total = toNumber(data?.totalRecords ?? 0);
  const totalPages = Math.max(1, toNumber(data?.totalPages ?? 1));
  const firstOnPage = total === 0 ? 0 : (resolved.page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(resolved.page * PAGE_SIZE, total);

  const columns = useMemo<ReadonlyArray<TableColumn<ShipmentDto>>>(
    () => [
      {
        id: 'plannedDate',
        header: 'Дата отгрузки',
        width: 'w-36',
        isSortable: true,
        cell: (shipment) => (
          <span className="font-[550] text-ink-900">
            {formatDate(shipment.plannedDate)}
          </span>
        ),
      },
      {
        id: 'partner',
        header: 'Клиент',
        cell: (shipment) => (
          <div>
            <div className="font-[550] text-ink-900">
              {shipment.partnerName}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-ink-500 tabular-nums">
              № {shipment.number}
              <ShipmentOriginBadge
                origin={shipment.origin}
                customerOrderNumber={shipment.customerOrderNumber}
                isForecast={shipment.isForecast}
              />
            </div>
          </div>
        ),
      },
      {
        id: 'lines',
        header: 'Товар',
        hideBelow: 'md',
        cell: (shipment) => <ShipmentLinesCell lines={shipment.lines} />,
      },
      {
        id: 'amount',
        header: 'Стоимость без НДС',
        width: 'w-40',
        align: 'right',
        cell: (shipment) => (
          <div>
            <div>{formatMoney(toNumber(shipment.amount))}</div>
            {shipment.hasLinesWithoutPrice && (
              <div className="text-[11.5px] text-st-approval">
                есть без цены
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        width: 'w-56',
        align: 'right',
        cell: (shipment) => (
          // Кнопки в строке не должны открывать карточку: клик по строке ведёт туда.
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            className="flex justify-end gap-1.5"
            onClick={(event) => event.stopPropagation()}
          >
            <Button
              size="sm"
              variant="ghost"
              title="Редактировать"
              aria-label={`Редактировать отгрузку № ${shipment.number}`}
              onClick={() => setEditingId(shipment.id)}
            >
              <Pencil aria-hidden className="size-3.5" />
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={takeIntoWork.isPending}
              onClick={() => takeIntoWork.mutate({ path: { id: shipment.id } })}
            >
              Взять в работу <ArrowRight aria-hidden className="size-3" />
            </Button>
          </div>
        ),
      },
    ],
    [takeIntoWork],
  );

  return (
    <>
      <TopbarActions>
        <Button onClick={() => setIsPlanningFromHistory(true)}>
          <History aria-hidden /> Сформировать из истории
        </Button>
        <Button variant="primary" onClick={() => setIsCreating(true)}>
          <Plus aria-hidden /> Добавить отгрузку
        </Button>
      </TopbarActions>

      <Alert tone="info" className="mb-4">
        Долгосрочный прогноз отгрузок на клиента. Когда до плановой даты
        остаётся меньше месяца, отгрузку берут в работу - пока кнопкой «Взять в
        работу», автоматический переход появится позже.
      </Alert>

      <div className="mb-3.5 flex flex-wrap items-center gap-2 rounded-lg border border-ink-200 bg-white px-3.5 py-3">
        <DebouncedSearchInput
          className="min-w-[220px] flex-1"
          placeholder="Поиск по клиенту..."
          aria-label="Поиск по клиенту"
          value={resolved.search}
          onCommit={(value) => setSearch({ search: value || undefined })}
        />
        <div
          role="group"
          aria-label="Период"
          className="inline-flex rounded-[7px] bg-ink-100 p-0.5"
        >
          {PERIODS.map((period) => (
            <button
              key={period.value}
              type="button"
              aria-pressed={resolved.period === period.value}
              onClick={() =>
                setSearch({
                  period: period.value === 'all' ? undefined : period.value,
                })
              }
              className={cn(
                'h-[30px] cursor-pointer rounded-md px-3 text-[12.5px] font-medium text-ink-600 hover:text-ink-900',
                resolved.period === period.value &&
                  'bg-white text-ink-900 shadow-sm',
              )}
            >
              {period.label}
            </button>
          ))}
        </div>
        {data !== undefined && (
          <span className="ml-auto text-xs text-ink-500">{total} в плане</span>
        )}
      </div>

      {takeIntoWork.isError && (
        <Alert tone="error" className="mb-3.5">
          Не удалось взять в работу:{' '}
          {resolveApiErrorMessage(takeIntoWork.error)}
        </Alert>
      )}

      {isError ? (
        <Alert tone="error">
          Не удалось загрузить план: {resolveApiErrorMessage(error)}.
        </Alert>
      ) : (
        <Table
          columns={columns}
          rows={data?.items ?? []}
          getRowKey={(shipment) => shipment.id}
          onRowClick={(shipment) =>
            void navigate({
              to: '/shipments/$shipmentId',
              params: { shipmentId: shipment.id },
            })
          }
          isLoading={isPending}
          sort={resolved.sort}
          onSortChange={toggleSort}
          // Клиент занимает две строки (название, номер с бейджем) - отступ
          // как у .plan-tbl макета, иначе строка в 44px ему тесна.
          cellClassName="py-3"
          emptyLabel={
            resolved.search === '' && resolved.period === 'all'
              ? 'В плане пока нет отгрузок - добавьте первую'
              : 'По выбранным фильтрам ничего не найдено'
          }
          footer={
            <TablePagination
              page={resolved.page}
              totalPages={totalPages}
              onPageChange={(page) =>
                setSearch({ page: page === 1 ? undefined : page }, false)
              }
              summary={
                total === 0
                  ? 'Ничего не найдено'
                  : `Показаны ${firstOnPage}-${lastOnPage} из ${total}`
              }
            />
          }
        />
      )}

      {isCreating && (
        <ShipmentEditorDialog onClose={() => setIsCreating(false)} />
      )}
      {isPlanningFromHistory && (
        <HistoryPlanDialog onClose={() => setIsPlanningFromHistory(false)} />
      )}
      {editingId !== null && (
        <EditShipmentDialog
          shipmentId={editingId}
          onClose={() => setEditingId(null)}
        />
      )}
    </>
  );
};
